import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Shield, AlertTriangle, Download, ExternalLink, Copy, Lock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ScriptInfo {
  id: string;
  src: string;
  type: 'inline' | 'external' | 'third-party';
  domain: string;
  size: number;
  contentHash: string;
  entropy: number;
  containsEval: boolean;
  containsFunction: boolean;
  isDynamic: boolean;
  securityScore: number;
  lastChecked: string;
  scanId: number;
}

interface ScriptInventoryData {
  scanId: number;
  url: string;
  totalScripts: number;
  scripts: ScriptInfo[];
  thirdPartyDomains: {
    domain: string;
    scriptCount: number;
    totalSize: number;
    avgSecurityScore: number;
    vendorName?: string | null;
    category?: string | null;
    riskTier?: string;
  }[];
  summary: {
    inlineScripts: number;
    externalScripts: number;
    thirdPartyScripts: number;
    totalSize: number;
    averageEntropy: number;
    securityRisks: number;
  };
}

export default function ScriptsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showRisksOnly, setShowRisksOnly] = useState(false);
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);

  // Get all scans to select from
  const { data: scans } = useQuery({
    queryKey: ["/api/scans"],
  });

  // Use the latest scan by default
  const scanToUse = selectedScanId || (Array.isArray(scans) && scans.length > 0 ? scans[0].id : null);

  const { data: scriptData, isLoading } = useQuery<ScriptInventoryData>({
    queryKey: ["/api/scans", scanToUse, "scripts"],
    enabled: !!scanToUse,
  });

  const { data: sriData } = useQuery<{ suggestions: { src: string; algorithm: string; integrity: string }[] }>({
    queryKey: ["/api/scans", scanToUse, "sri-suggestions"],
    queryFn: () => apiRequest(`/api/scans/${scanToUse}/sri-suggestions`),
    enabled: !!scanToUse,
  });
  const { toast } = useToast();

  // Determine if a script has security risks
  const hasSecurityRisk = (script: ScriptInfo) => {
    return script.containsEval || 
           script.containsFunction || 
           script.isDynamic || 
           script.securityScore < 70 ||
           script.entropy > 6;
  };

  const filteredScripts = scriptData?.scripts?.filter(script => {
    const matchesSearch = script.src.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         script.domain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || script.type === filterType;
    const matchesRiskFilter = !showRisksOnly || hasSecurityRisk(script);
    return matchesSearch && matchesType && matchesRiskFilter;
  }) || [];

  const getSecurityColor = (score: number) => {
    if (score >= 85) return "text-purple-600 dark:text-purple-400";
    if (score >= 70) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'inline': return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case 'external': return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case 'third-party': return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Script Inventory</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!scriptData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Script Inventory</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-600 dark:text-gray-400">No scan data available. Please run a scan first.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Script Inventory</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Complete inventory of JavaScript files detected on {scriptData.url}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            if (scanToUse) {
              // Create a temporary anchor element to trigger download
              const link = document.createElement('a');
              link.href = `/api/export/scripts/${scanToUse}`;
              link.download = `script-inventory-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }}
          data-testid="button-export-scripts"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Cards - Now Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200"
          onClick={() => setFilterType("all")}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {scriptData.totalScripts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Scripts</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200"
          onClick={() => setFilterType("inline")}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {scriptData.summary.inlineScripts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Inline</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200"
          onClick={() => setFilterType("external")}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {scriptData.summary.externalScripts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">External</div>
          </CardContent>
        </Card>
        <Card 
          className="cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200"
          onClick={() => setFilterType("third-party")}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {scriptData.summary.thirdPartyScripts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Third-Party</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {Math.round(scriptData.summary.totalSize / 1024)}KB
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Size</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer hover:shadow-md hover:scale-105 transition-all duration-200 ${showRisksOnly ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
          onClick={() => {
            setShowRisksOnly(!showRisksOnly);
            // Reset type filter when showing risks only
            if (!showRisksOnly) {
              setFilterType("all");
            }
          }}
        >
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {scriptData.summary.securityRisks}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Risk Alerts {showRisksOnly && "(Active)"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search scripts by URL or domain..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterType} onValueChange={(value) => {
          setFilterType(value);
          // Reset risk filter when changing type filter
          setShowRisksOnly(false);
        }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scripts</SelectItem>
            <SelectItem value="inline">Inline Scripts</SelectItem>
            <SelectItem value="external">External Scripts</SelectItem>
            <SelectItem value="third-party">Third-Party Scripts</SelectItem>
          </SelectContent>
        </Select>
        {showRisksOnly && (
          <Button
            variant="outline"
            onClick={() => setShowRisksOnly(false)}
            className="flex items-center gap-2"
          >
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Clear Risk Filter
          </Button>
        )}
      </div>

      {/* Scripts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            JavaScript Files ({filteredScripts.length})
            {showRisksOnly && (
              <Badge variant="destructive" className="ml-2">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Risk Alerts Only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredScripts.map((script) => (
              <div
                key={script.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getTypeColor(script.type)}>
                        {script.type}
                      </Badge>
                      <span className={`text-sm font-medium ${getSecurityColor(script.securityScore)}`}>
                        Security: {script.securityScore}%
                      </span>
                      {script.containsEval && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          eval()
                        </Badge>
                      )}
                      {script.containsFunction && (
                        <Badge variant="outline" className="text-xs">
                          Function()
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {script.src}
                        </span>
                        {script.src.startsWith('http') && (
                          <ExternalLink className="h-3 w-3 text-gray-400" />
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Domain:</span> {script.domain} • 
                        <span className="font-medium"> Size:</span> {Math.round(script.size / 1024)}KB • 
                        <span className="font-medium"> Entropy:</span> {script.entropy.toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        Hash: {script.contentHash.slice(0, 16)}...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Third-Party Domains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Third-Party Domains ({scriptData.thirdPartyDomains.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scriptData.thirdPartyDomains.map((domain, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate">
                    {domain.domain}
                  </h3>
                  <Badge variant="outline">
                    {domain.scriptCount} scripts
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <div>Size: {Math.round(domain.totalSize / 1024)}KB</div>
                  <div className={`font-medium ${getSecurityColor(domain.avgSecurityScore)}`}>
                    Avg Security: {domain.avgSecurityScore}%
                  </div>
                  {domain.vendorName && <div>Vendor: {domain.vendorName}</div>}
                  {domain.category && <div className="text-muted-foreground">{domain.category}</div>}
                  {domain.riskTier && (
                    <Badge variant={domain.riskTier === "high" || domain.riskTier === "critical" ? "destructive" : "secondary"} className="mt-1">
                      Risk: {domain.riskTier}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SRI (Subresource Integrity) suggestions */}
      {sriData?.suggestions && sriData.suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              SRI suggestions
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Add these integrity attributes to your script tags to prevent tampering. Copy and add to the script element: <code className="bg-muted px-1 rounded">integrity=&quot;...&quot;</code>
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sriData.suggestions.map((s, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap rounded border p-2 text-sm">
                  <span className="truncate flex-1 min-w-0 font-mono" title={s.src}>{s.src}</span>
                  <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-md" title={s.integrity}>{s.integrity}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(s.integrity);
                      toast({ title: "Copied integrity value" });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}